<template>
  <div class="min-h-screen bg-gradient-to-b from-[#142a4a] via-[#18365f] to-[#1b4475]">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-10">
      <div class="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 class="text-3xl font-bold text-slate-100">Registo de logs</h1>
          <p class="text-slate-400 mt-1 text-sm">
            Histórico de logs de execuções dos agentes.
          </p>
        </div>
        <button
          type="button"
          class="shrink-0 px-4 py-2 rounded-lg border border-cyan-400/45 text-cyan-100 text-sm font-medium hover:bg-cyan-400/10 transition"
          @click="goDashboard"
        >
          Painel de execuções
        </button>
      </div>

        <button
          type="button"
          class="sm:hidden mb-2 w-full flex items-center justify-between gap-2 rounded-xl border border-cyan-400/25 bg-slate-900/60 px-4 py-3 text-left text-sm font-medium text-slate-100 shadow-sm shadow-black/20"
          :aria-expanded="logFiltersExpanded ? 'true' : 'false'"
          aria-controls="agent-run-logs-filters-panel"
          @click="logFiltersOpen = !logFiltersOpen"
        >
          <span>Filtros</span>
          <svg
            class="w-5 h-5 text-cyan-300 shrink-0 transition-transform duration-200"
            :class="{ 'rotate-180': logFiltersOpen }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div
          id="agent-run-logs-filters-panel"
          v-show="logFiltersExpanded"
          class="mb-6 rounded-xl border border-cyan-400/25 bg-slate-900/60 p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-3"
        >
          <div>
            <label class="block text-[11px] uppercase text-white mb-1">ID</label>
            <input
              v-model="filterId"
              type="text"
              inputmode="numeric"
              placeholder="ex.: 1124"
              class="agent-run-logs-filter-control w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-sm text-slate-100 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
            />
          </div>
          <div>
            <label class="block text-[11px] uppercase text-white mb-1">Coordenador</label>
            <select
              v-model="filterCoordinatorId"
              class="agent-run-logs-filter-control w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
            >
              <option value="">Todos</option>
              <option v-for="c in coordinators" :key="c.id" :value="String(c.id)">{{ c.name }}</option>
            </select>
          </div>
          <div>
            <label class="block text-[11px] uppercase text-white mb-1">Agente (executor)</label>
            <select
              v-model="filterAgentId"
              class="agent-run-logs-filter-control w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
            >
              <option value="">Todos</option>
              <option v-for="a in executors" :key="a.id" :value="String(a.id)">{{ a.name }}</option>
            </select>
          </div>
          <div>
            <label class="block text-[11px] uppercase text-white mb-1">Usuário</label>
            <select
              v-model="filterUserId"
              class="agent-run-logs-filter-control w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
            >
              <option value="">Todos</option>
              <option v-for="u in logFilterUsers" :key="u.id" :value="String(u.id)">{{ u.label }}</option>
            </select>
          </div>
          <div>
            <label class="block text-[11px] uppercase text-white mb-1">Status</label>
            <select
              v-model="filterStatus"
              class="agent-run-logs-filter-control w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
            >
              <option value="">Todos</option>
              <option value="queued">{{ runStatusLabel('queued') }}</option>
              <option value="running">{{ runStatusLabel('running') }}</option>
              <option value="succeeded">{{ runStatusLabel('succeeded') }}</option>
              <option value="failed">{{ runStatusLabel('failed') }}</option>
              <option value="cancelled">{{ runStatusLabel('cancelled') }}</option>
            </select>
          </div>
          <div>
            <label class="block text-[11px] uppercase text-white mb-1">Data inicial</label>
            <input
              v-model="filterDateFrom"
              type="date"
              class="agent-run-logs-date-input agent-run-logs-filter-control w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
            />
          </div>
          <div>
            <label class="block text-[11px] uppercase text-white mb-1">Data final</label>
            <input
              v-model="filterDateTo"
              type="date"
              class="agent-run-logs-date-input agent-run-logs-filter-control w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
            />
          </div>
          <div class="md:col-span-2 lg:col-span-3 xl:col-span-4 2xl:col-span-7 flex flex-wrap gap-2 items-end justify-end">
            <button
              type="button"
              class="px-4 py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/50 text-cyan-100 text-sm hover:bg-cyan-500/30"
              :disabled="listLoading"
              @click="applyFilters"
            >
              Aplicar filtros
            </button>
            <button
              type="button"
              class="px-4 py-2 rounded-lg border border-slate-600 text-slate-300 text-sm hover:bg-slate-800/80"
              @click="clearFilters"
            >
              Limpar
            </button>
          </div>
        </div>

        <div v-if="listError" class="mb-4 p-3 rounded-lg border border-red-400/40 bg-red-950/30 text-red-200 text-sm">
          {{ listError }}
        </div>

        <div class="rounded-xl border border-cyan-400/20 bg-slate-900/50 overflow-hidden">
          <!-- Mobile: cartões (estilo index de usuários / CrudBase) -->
          <div class="sm:hidden">
            <div v-if="listLoading" class="px-4 py-10 text-center text-slate-500 text-sm">A carregar…</div>
            <div v-else-if="!rows.length" class="px-4 py-10 text-center text-slate-500 text-sm">
              Nenhuma execução encontrada.
            </div>
            <div v-else class="divide-y divide-slate-700/70">
              <div
                v-for="r in rows"
                :key="r.id"
                class="p-4 bg-slate-900/30 transition hover:bg-cyan-400/5"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p class="text-sm font-mono font-semibold text-cyan-200/90">#{{ r.id }}</p>
                    <p class="text-xs text-slate-400 mt-1">
                      {{ formatIsoTimeCompact(r.created_at) }}
                      <span v-if="r.finished_at" class="block text-slate-500 mt-0.5">
                        Término {{ formatIsoTimeCompact(r.finished_at) }}
                      </span>
                    </p>
                  </div>
                  <button
                    type="button"
                    class="shrink-0 inline-flex items-center justify-center px-3 py-1.5 rounded-lg border border-cyan-400/45 bg-cyan-400/5 text-cyan-200 text-xs font-medium hover:bg-cyan-400/12"
                    aria-label="Ver detalhes da execução"
                    @click="openDetail(r.id)"
                  >
                    Ver
                  </button>
                </div>
                <div class="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <div class="text-slate-400">Agente</div>
                  <div class="text-slate-100 font-medium text-right break-words">{{ r.agent_name || '—' }}</div>
                  <div class="text-slate-400">Coordenador</div>
                  <div class="text-slate-100 font-medium text-right break-words">{{ r.coordinator_name || '—' }}</div>
                  <div class="text-slate-400">Usuário</div>
                  <div class="text-slate-100 font-medium text-right break-words">{{ r.user_label || '—' }}</div>
                  <div class="text-slate-400">Trigger</div>
                  <div class="text-slate-100 font-medium text-right break-all">{{ r.trigger }}</div>
                  <div class="text-slate-400">Status</div>
                  <div class="text-right">
                    <span class="text-xs px-2 py-0.5 rounded-full border inline-block" :class="logStatusBadgeClass(r.status)">
                      {{ runStatusLabel(r.status) }}
                    </span>
                  </div>
                  <div class="text-slate-400">Registos</div>
                  <div class="text-slate-100 font-medium text-right tabular-nums">
                    <span class="inline-flex items-center justify-end gap-1.5 flex-wrap">
                      <span>{{ bulkImportRegistosLabel(r) }}</span>
                      <span
                        v-if="bulkImportRegistosHasPartialFailure(r)"
                        class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full border bg-red-500/15 text-red-200 border-red-400/45"
                        :title="bulkImportRegistosTitle(r)"
                      >
                        {{ bulkImportRegistosFailedBadgeCount(r) }}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Desktop: tabela -->
          <div class="hidden sm:block overflow-x-auto">
            <table class="min-w-full text-sm text-left text-slate-200">
              <thead class="text-[11px] uppercase text-white border-b border-slate-700 bg-slate-900/80">
                <tr>
                  <th class="px-3 py-2 font-medium">ID</th>
                  <th class="px-3 py-2 font-medium">Criada</th>
                  <th class="px-3 py-2 font-medium">Agente</th>
                  <th class="px-3 py-2 font-medium">Coordenador</th>
                  <th class="px-3 py-2 font-medium">Usuário</th>
                  <th class="px-3 py-2 font-medium">Trigger</th>
                  <th class="px-3 py-2 font-medium">Status</th>
                  <th class="px-3 py-2 font-medium whitespace-nowrap">Registos</th>
                  <th class="px-3 py-2 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="listLoading" class="border-b border-slate-800">
                  <td colspan="9" class="px-3 py-8 text-center text-slate-500">A carregar…</td>
                </tr>
                <tr v-else-if="!rows.length" class="border-b border-slate-800">
                  <td colspan="9" class="px-3 py-8 text-center text-slate-500">Nenhuma execução encontrada.</td>
                </tr>
                <tr
                  v-for="r in rows"
                  v-else
                  :key="r.id"
                  class="border-b border-slate-800/80 hover:bg-cyan-400/10"
                >
                  <td class="px-3 py-2 font-mono text-cyan-200/90">#{{ r.id }}</td>
                  <td class="px-3 py-2 text-slate-400 whitespace-nowrap align-top">
                    <div>{{ formatIsoTimeCompact(r.created_at) }}</div>
                    <div v-if="r.finished_at" class="text-slate-500 text-[11px] mt-1 leading-tight">
                      Término {{ formatIsoTimeCompact(r.finished_at) }}
                    </div>
                  </td>
                  <td class="px-3 py-2 text-slate-300 max-w-[10rem] truncate" :title="r.agent_name || ''">
                    {{ r.agent_name || '—' }}
                  </td>
                  <td class="px-3 py-2 text-slate-300 max-w-[10rem] truncate" :title="r.coordinator_name || ''">
                    {{ r.coordinator_name || '—' }}
                  </td>
                  <td class="px-3 py-2 text-slate-300 max-w-[10rem] truncate" :title="r.user_label || ''">
                    {{ r.user_label || '—' }}
                  </td>
                  <td class="px-3 py-2 text-slate-400">{{ r.trigger }}</td>
                  <td class="px-3 py-2">
                    <span class="text-xs px-2 py-0.5 rounded-full border" :class="logStatusBadgeClass(r.status)">
                      {{ runStatusLabel(r.status) }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-slate-300 tabular-nums whitespace-nowrap">
                    <span
                      class="inline-flex items-center gap-1.5 flex-wrap"
                      :title="bulkImportRegistosTitle(r) || undefined"
                    >
                      <span>{{ bulkImportRegistosLabel(r) }}</span>
                      <span
                        v-if="bulkImportRegistosHasPartialFailure(r)"
                        class="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full border shrink-0 bg-red-500/15 text-red-200 border-red-400/45"
                        :title="bulkImportRegistosTitle(r)"
                      >
                        {{ bulkImportRegistosFailedBadgeCount(r) }}
                      </span>
                    </span>
                  </td>
                  <td class="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      class="inline-flex items-center justify-center p-1.5 rounded-md text-cyan-300 hover:text-cyan-100 hover:bg-cyan-400/10 transition"
                      aria-label="Ver detalhes da execução"
                      @click="openDetail(r.id)"
                    >
                      <i class="fas fa-search text-sm" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="pagy && pagy.count > 0"
            class="px-3 py-3 border-t border-slate-700 flex flex-wrap items-center gap-2 text-xs text-slate-400"
          >
            <span>{{ pagy.from }}–{{ pagy.to }} de {{ pagy.count }}</span>
            <button
              type="button"
              class="px-2 py-1 rounded border border-slate-600 disabled:opacity-40"
              :disabled="listLoading || !pagy.prev"
              @click="goPage(pagy.prev)"
            >
              Anterior
            </button>
            <span>Pág. {{ pagy.page }}/{{ pagy.pages }}</span>
            <button
              type="button"
              class="px-2 py-1 rounded border border-slate-600 disabled:opacity-40"
              :disabled="listLoading || !pagy.next"
              @click="goPage(pagy.next)"
            >
              Próxima
            </button>
          </div>
        </div>

    <!-- Modal detalhe da execução -->
    <div
      v-if="detailModalOpen"
      class="fixed inset-0 z-[90] flex items-start justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="detailModalTitleId"
    >
      <div
        class="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]"
        @click="closeDetailModal"
      />
      <div
        class="relative w-full max-w-4xl max-h-[min(78vh,720px)] mt-6 sm:mt-10 rounded-xl border border-cyan-400/30 bg-[#2f4563] shadow-2xl shadow-black/50 flex flex-col overflow-hidden"
      >
        <div class="shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-cyan-200/20 bg-[#243a56]">
          <h2 :id="detailModalTitleId" class="text-lg font-semibold text-slate-100 truncate pr-2">
            <template v-if="runDetail">Execução #{{ runDetail.id }}</template>
            <template v-else>Detalhe da execução</template>
          </h2>
          <button
            type="button"
            class="shrink-0 p-2 rounded-lg text-slate-400 hover:text-cyan-200 hover:bg-white/5"
            aria-label="Fechar"
            @click="closeDetailModal"
          >
            ✕
          </button>
        </div>
        <div class="agent-run-logs-detail-modal-body flex-1 min-h-0 overflow-y-auto px-4 py-4 text-sm">
          <div v-if="detailError" class="mb-3 p-3 rounded-lg border border-red-400/40 bg-red-950/30 text-red-200 text-sm">
            {{ detailError }}
          </div>
          <div v-else-if="detailLoading" class="text-slate-400 py-8 text-center">A carregar detalhe…</div>
          <template v-else-if="runDetail">
            <div class="mb-4 flex flex-wrap items-center gap-3">
              <p class="text-xl font-mono font-bold text-cyan-200 tabular-nums">#{{ runDetail.id }}</p>
              <span
                class="text-xs px-2 py-0.5 rounded-full border"
                :class="logStatusBadgeClass(runDetail.status)"
              >{{ runStatusLabel(runDetail.status) }}</span>
              <button
                v-if="canRequeue(runDetail.status)"
                type="button"
                class="px-3 py-1.5 rounded-lg text-xs font-medium border border-amber-400/50 text-amber-200 hover:bg-amber-400/10 disabled:opacity-40"
                :disabled="requeueLoading"
                @click="openRequeueConfirm"
              >
                Enviar para fila novamente
              </button>
            </div>
            <p v-if="requeueMessage" class="mb-3 text-sm text-emerald-300">{{ requeueMessage }}</p>
            <p v-if="requeueError" class="mb-3 text-sm text-red-300">{{ requeueError }}</p>
            <AgentRunDetailBody :run-detail="runDetail" />
          </template>
        </div>
      </div>
    </div>

    <!-- Confirmação reenfileirar (estilo alinhado ao salvar em CrudBase) -->
    <div
      v-if="requeueConfirmOpen"
      class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="agent-run-requeue-confirm-title"
      @click.self="closeRequeueConfirm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
        <h3 id="agent-run-requeue-confirm-title" class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z" clip-rule="evenodd" />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">
          Deseja enviar esta execução para a fila novamente? Será criada uma nova tentativa com o mesmo contexto.
        </p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="requeueLoading"
            class="px-4 py-2 bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold rounded-lg transition disabled:opacity-50"
            @click="confirmRequeue"
          >
            {{ requeueLoading ? 'A enviar…' : 'Sim' }}
          </button>
          <button
            type="button"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition"
            :disabled="requeueLoading"
            @click="closeRequeueConfirm"
          >
            Não
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { navigateTo } from '../router.js'
import { useApi } from '../composables/useApi'
import { useAgentUi } from '../composables/useAgentUi'
import AgentRunDetailBody from './AgentRunDetailBody.vue'

const LIST_ITEMS = 25

export default {
  components: { AgentRunDetailBody },

  setup() {
    const { get, post } = useApi()
    const { runStatusLabel, formatIsoTimeCompact } = useAgentUi()
    return { get, post, runStatusLabel, formatIsoTimeCompact }
  },

  data() {
    return {
      detailModalOpen: false,
      detailModalTitleId: 'agent-run-log-detail-title',
      dashboardCards: [],
      logFilterUsers: [],
      filterId: '',
      filterCoordinatorId: '',
      filterAgentId: '',
      filterUserId: '',
      filterStatus: '',
      filterDateFrom: '',
      filterDateTo: '',
      listPage: 1,
      rows: [],
      pagy: null,
      listLoading: false,
      listError: null,
      runDetail: null,
      detailLoading: false,
      detailError: null,
      requeueLoading: false,
      requeueConfirmOpen: false,
      requeueMessage: null,
      requeueError: null,
      /** Acordeão filtros: fechado no telemóvel; a partir do breakpoint sm o painel fica sempre visível. */
      logFiltersOpen: false,
      logFiltersMqDesktop:
        typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches
    }
  },

  computed: {
    logFiltersExpanded() {
      return this.logFiltersMqDesktop || this.logFiltersOpen
    },
    coordinators() {
      return (this.dashboardCards || []).filter((c) => c.agent_type === 'coordinator')
    },
    executors() {
      return (this.dashboardCards || []).filter((c) => c.agent_type === 'executor')
    }
  },

  mounted() {
    this.syncLogFiltersBreakpoint()
    window.addEventListener('resize', this.syncLogFiltersBreakpoint)
    this.loadDashboardCards()
    this.loadLogFilterUsers()
    this.fetchList()
    this._detailModalKeydown = (e) => {
      if (e.key !== 'Escape') return
      if (this.requeueConfirmOpen) {
        if (!this.requeueLoading) this.closeRequeueConfirm()
        return
      }
      if (this.detailModalOpen) this.closeDetailModal()
    }
    window.addEventListener('keydown', this._detailModalKeydown)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.syncLogFiltersBreakpoint)
    if (this._detailModalKeydown) {
      window.removeEventListener('keydown', this._detailModalKeydown)
    }
  },

  methods: {
    syncLogFiltersBreakpoint() {
      if (typeof window === 'undefined') return
      const desktop = window.matchMedia('(min-width: 640px)').matches
      this.logFiltersMqDesktop = desktop
      if (!desktop) this.logFiltersOpen = false
    },

    bulkImportRegistosLabel(r) {
      const total = r.bulk_import_total
      if (total == null) return '—'
      const ok = r.bulk_import_ok ?? 0
      return `${ok}/${total}`
    },

    bulkImportRegistosTitle(r) {
      if (r.bulk_import_total == null) return ''
      const failed = r.bulk_import_failed
      if (failed == null || failed === 0) return ''
      return `${failed} com erro`
    },

    bulkImportRegistosFailedBadgeCount(r) {
      const failed = r.bulk_import_failed
      if (failed != null && failed > 0) return failed
      const total = r.bulk_import_total ?? 0
      const ok = r.bulk_import_ok ?? 0
      return Math.max(0, total - ok)
    },

    bulkImportRegistosHasPartialFailure(r) {
      const total = r.bulk_import_total
      if (total == null || total <= 0) return false
      return this.bulkImportRegistosFailedBadgeCount(r) > 0
    },

    async loadDashboardCards() {
      const { data, error } = await this.get('/api/v1/agents/dashboard')
      if (!error && data?.data) {
        this.dashboardCards = data.data
      }
    },

    async loadLogFilterUsers() {
      const { data, error } = await this.get('/api/v1/users/log_filter_options')
      if (!error && Array.isArray(data?.data)) {
        this.logFilterUsers = data.data
      }
    },

    buildListQuery(page) {
      const p = new URLSearchParams()
      p.set('items', String(LIST_ITEMS))
      p.set('page', String(page || 1))
      if (this.filterId.trim()) p.set('id', this.filterId.trim())
      if (this.filterCoordinatorId) p.set('coordinator_agent_id', this.filterCoordinatorId)
      if (this.filterAgentId) p.set('agent_id', this.filterAgentId)
      if (this.filterUserId) p.set('user_id', this.filterUserId)
      if (this.filterStatus) p.set('status', this.filterStatus)
      if (this.filterDateFrom) p.set('created_at_from', this.filterDateFrom)
      if (this.filterDateTo) p.set('created_at_to', this.filterDateTo)
      return p.toString()
    },

    applyFilters() {
      this.listPage = 1
      this.fetchList()
      if (!this.logFiltersMqDesktop) this.logFiltersOpen = false
    },

    clearFilters() {
      this.filterId = ''
      this.filterCoordinatorId = ''
      this.filterAgentId = ''
      this.filterUserId = ''
      this.filterStatus = ''
      this.filterDateFrom = ''
      this.filterDateTo = ''
      this.listPage = 1
      this.fetchList()
      if (!this.logFiltersMqDesktop) this.logFiltersOpen = false
    },

    goPage(page) {
      if (page == null || page < 1) return
      this.listPage = page
      this.fetchList()
    },

    async fetchList() {
      this.listLoading = true
      this.listError = null
      const qs = this.buildListQuery(this.listPage)
      const { data, error } = await this.get(`/api/v1/agent_runs?${qs}`)
      this.listLoading = false
      if (error) {
        this.listError = 'Não foi possível carregar os logs.'
        this.rows = []
        this.pagy = null
        return
      }
      this.rows = data.data || []
      this.pagy = data.pagy || null
      if (this.pagy?.page) this.listPage = this.pagy.page
    },

    openDetail(id) {
      this.requeueMessage = null
      this.requeueError = null
      this.detailModalOpen = true
      this.loadDetail(id)
    },

    closeDetailModal() {
      this.detailModalOpen = false
      this.requeueConfirmOpen = false
      this.runDetail = null
      this.detailError = null
      this.detailLoading = false
      this.requeueMessage = null
      this.requeueError = null
    },

    openRequeueConfirm() {
      if (!this.runDetail?.id || !this.canRequeue(this.runDetail.status)) return
      this.requeueMessage = null
      this.requeueError = null
      this.requeueConfirmOpen = true
    },

    closeRequeueConfirm() {
      if (this.requeueLoading) return
      this.requeueConfirmOpen = false
    },

    async confirmRequeue() {
      if (!this.runDetail?.id) {
        this.requeueConfirmOpen = false
        return
      }
      await this.requeueRow(this.runDetail.id, { reloadDetail: true })
      this.requeueConfirmOpen = false
    },

    goDashboard() {
      navigateTo('agent-dashboard', {})
    },

    async loadDetail(id) {
      this.detailLoading = true
      this.detailError = null
      this.runDetail = null
      const { data, error } = await this.get(`/api/v1/agent_runs/${id}`)
      this.detailLoading = false
      if (error) {
        this.detailError = 'Não foi possível carregar o detalhe desta execução.'
        return
      }
      this.runDetail = data
    },

    logStatusBadgeClass(status) {
      if (status === 'running') return 'bg-emerald-500/15 text-emerald-300 border-emerald-400/35'
      if (status === 'succeeded') return 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20'
      if (status === 'failed') return 'bg-red-500/10 text-red-300 border-red-400/25'
      if (status === 'queued') return 'bg-amber-500/10 text-amber-200 border-amber-400/25'
      return 'bg-slate-800/80 text-slate-300 border-slate-600'
    },

    canRequeue(status) {
      return status === 'failed' || status === 'cancelled'
    },

    async requeueRow(id, opts = {}) {
      this.requeueLoading = true
      this.requeueMessage = null
      this.requeueError = null
      const { data, error } = await this.post(`/api/v1/agent_runs/${id}/requeue`, {})
      this.requeueLoading = false
      if (error) {
        const msg = error.response?.data?.errors?.[0] || 'Falha ao reenfileirar.'
        this.requeueError = msg
        return
      }
      if (data?.data?.status) {
        this.requeueMessage = `Execução #${id} enviada para a fila (estado: ${this.runStatusLabel(data.data.status)}).`
      }
      if (opts.reloadDetail && this.detailModalOpen && this.runDetail?.id === id) {
        await this.loadDetail(id)
      }
      this.fetchList()
    }
  }
}
</script>

<style scoped>
/* Conteúdo dos selects (lista nativa) legível em tema escuro */
.agent-run-logs-filter-control option {
  background-color: rgb(15 23 42);
  color: rgb(148 163 184);
}

/* Ícone do calendário dos inputs type="date" — tom acinzentado (WebKit/Blink) */
.agent-run-logs-date-input {
  color-scheme: dark;
}

.agent-run-logs-date-input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.4;
  filter: grayscale(1);
}

.agent-run-logs-date-input::-webkit-calendar-picker-indicator:hover {
  opacity: 0.65;
}

/* Scrollbar do corpo do modal de detalhe (tema escuro) */
.agent-run-logs-detail-modal-body {
  scrollbar-width: thin;
  scrollbar-color: rgba(34, 211, 238, 0.45) rgba(15, 23, 42, 0.55);
}

.agent-run-logs-detail-modal-body::-webkit-scrollbar {
  width: 8px;
}

.agent-run-logs-detail-modal-body::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.5);
  border-radius: 9999px;
  margin: 4px 0;
}

.agent-run-logs-detail-modal-body::-webkit-scrollbar-thumb {
  background: rgba(34, 211, 238, 0.35);
  border-radius: 9999px;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.agent-run-logs-detail-modal-body::-webkit-scrollbar-thumb:hover {
  background: rgba(34, 211, 238, 0.55);
  border: 2px solid transparent;
  background-clip: padding-box;
}
</style>
